import { useEffect, useState } from "react";
import { useEmailAccount } from "../../hooks/useEmailAccount";
import { useHospitals } from "../../hooks/useHospitals";
import { useCompanies } from "../../hooks/useCompanies";
import { useReservas } from "../../hooks/useReservas";
import { useSendEmail } from "./useSendEmail";
import { useSession } from "../../context/SessionContext";
import { notify } from "../../components/ui";
import { SUCCESS_MESSAGES } from "../../i18n/success.pt-BR";
import { t } from "../../i18n";
import { Hospital } from "../../types/hospital.types";
import { Company } from "../../types/company.types";
import { Reserva } from "../../types/reserva.types";
import { buildDefaultBody, buildDefaultSubject } from "./email";

type LoadStatus = "loading" | "connected" | "not-connected" | "error";

const toggled = (set: Set<number>, id: number) => {
  const next = new Set(set);
  if (next.has(id)) next.delete(id);
  else next.add(id);
  return next;
};

// Acrescenta o item recém-vinculado (ou atualizado) à lista, sem duplicar.
const upsert = <T extends { id: number }>(list: T[], item: T) =>
  list.some((x) => x.id === item.id) ? list.map((x) => (x.id === item.id ? item : x)) : [...list, item];

/** Estado do "Enviar por e-mail": vínculos carregados, seleção, anexos, mensagem e envio. */
export function useEmailComposer(patientName: string | undefined, onSent: () => void) {
  const { metadata } = useSession();
  const { fetchStatus, connectGoogle, connecting } = useEmailAccount();
  const { listMyHospitals } = useHospitals();
  const { listMyCompanies } = useCompanies();
  const { listMyReservas } = useReservas();
  const { sendEmail, sending } = useSendEmail();

  const [status, setStatus] = useState<LoadStatus>("loading");
  const [step, setStep] = useState<1 | 2>(1);
  const [hospitals, setHospitals] = useState<Hospital[]>([]);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [reservas, setReservas] = useState<Reserva[]>([]);
  const [hospitalIds, setHospitalIds] = useState<Set<number>>(new Set());
  const [companyIds, setCompanyIds] = useState<Set<number>>(new Set());
  const [reservaIds, setReservaIds] = useState<Set<number>>(new Set());
  const [files, setFiles] = useState<File[]>([]);
  const [subject, setSubject] = useState(() => buildDefaultSubject(patientName));
  const [body, setBody] = useState(() => buildDefaultBody(metadata.nome, patientName));
  // Enquanto o médico não editar o texto, ele acompanha as reservas selecionadas.
  const [bodyDirty, setBodyDirty] = useState(false);
  // Os erros de "falta destinatário/PDF" só aparecem depois de tentar continuar.
  const [tried, setTried] = useState(false);
  const [confirmNoReserva, setConfirmNoReserva] = useState(false);

  useEffect(() => {
    (async () => {
      const [account, h, c, r] = await Promise.all([fetchStatus(), listMyHospitals(), listMyCompanies(), listMyReservas()]);
      if (h) setHospitals(h.hospitals);
      if (c) setCompanies(c.companies);
      if (r) setReservas(r.reservas);
      setStatus(account ? (account.connected ? "connected" : "not-connected") : "error");
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const selectedHospitals = hospitals.filter((h) => hospitalIds.has(h.id));
  const selectedCompanies = companies.filter((c) => companyIds.has(c.id));
  const selectedReservas = reservas.filter((r) => reservaIds.has(r.id));

  useEffect(() => {
    if (!bodyDirty) setBody(buildDefaultBody(metadata.nome, patientName, selectedReservas.map((r) => r.name)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reservaIds, reservas]);

  const missing = {
    recipient: hospitalIds.size + companyIds.size === 0,
    files: files.length === 0,
    subject: !subject.trim(),
  };
  const canContinue = !missing.recipient && !missing.files && !missing.subject;

  const handleContinue = () => {
    setTried(true);
    if (!canContinue) return;
    if (reservaIds.size === 0) setConfirmNoReserva(true);
    else setStep(2);
  };

  const continueWithoutReserva = () => {
    setConfirmNoReserva(false);
    setStep(2);
  };

  const handleSend = async () => {
    const outcome = await sendEmail(Array.from(hospitalIds), Array.from(companyIds), files, subject, body);
    if (!outcome) return; // os demais erros já viraram toast no `request`

    if (outcome.status === "sent") {
      notify({ tone: "success", title: SUCCESS_MESSAGES.emailSent, body: t("sharing.email.sentTo", { to: outcome.result.sent_to.join(", ") }) });
      onSent();
      return;
    }
    notify(
      outcome.status === "not-connected"
        ? { tone: "info", title: t("sharing.email.notConnected") }
        : { tone: "error", title: outcome.message ?? t("sharing.email.sendFailed") }
    );
    setStatus("not-connected");
    setStep(1);
  };

  return {
    status, step, setStep, connectGoogle, connecting, sending,
    hospitals, companies, reservas,
    hospitalIds, companyIds, reservaIds,
    selectedHospitals, selectedCompanies, selectedReservas,
    toggleHospital: (id: number) => setHospitalIds((s) => toggled(s, id)),
    toggleCompany: (id: number) => setCompanyIds((s) => toggled(s, id)),
    toggleReserva: (id: number) => setReservaIds((s) => toggled(s, id)),
    // Vincular pelo formulário já seleciona o item.
    linkHospital: (h: Hospital) => { setHospitals((l) => upsert(l, h)); setHospitalIds((s) => new Set(s).add(h.id)); },
    linkCompany: (c: Company) => { setCompanies((l) => upsert(l, c)); setCompanyIds((s) => new Set(s).add(c.id)); },
    linkReserva: (r: Reserva) => { setReservas((l) => upsert(l, r)); setReservaIds((s) => new Set(s).add(r.id)); },
    files, addFiles: (added: File[]) => setFiles((f) => [...f, ...added]), removeFile: (i: number) => setFiles((f) => f.filter((_, j) => j !== i)),
    subject, setSubject,
    body, setBody: (value: string) => { setBody(value); setBodyDirty(true); }, bodyDirty,
    tried, missing, handleContinue, handleSend,
    confirmNoReserva, setConfirmNoReserva, continueWithoutReserva,
  };
}

export type EmailComposerState = ReturnType<typeof useEmailComposer>;
